import {
  CheckIcon,
  DeleteIcon,
  Edit1Icon,
  GKButton,
  GKSearchSelect,
  GKTextField,
  Grid2,
  IconButton,
  PercentageIcon,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useAppSelector } from "store/store";
import axiosInstance from "utils/axiosInstance";
import { Cell, TableBodyCell } from "../utils";

export interface PartnerList {
  id: string;
  partner: number;
  feePlan: string;
  managementFeeSharingPercentage: number;
  performanceFeeSharingPercentage: number;
}

const PartnerHistory = () => {
  const partner = useAppSelector((state) => state?.app?.partnerList);

  const { id } = useParams();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const { data, refetch } = useFetchQuery({
    key: ["REVENUE_SHARING"],
    route: "/api/revenue/revenue-sharing/",
    requestBody: { fee_plan: id },
    success: (response: any) => {
      if (response?.data?.data.length === 0) {
        setIsCreating(true);
      }
    },
  });

  const form = useForm({
    defaultValues: {
      partner: "",
      managementFeeSharingPercentage: 0,
      performanceFeeSharingPercentage: 0,
    },
  });

  const createPartnerSharing = useMutation({
    mutationKey: ["CREATE_PARTNER_SHARING"],
    mutationFn: (data) =>
      axiosInstance.post(`/api/revenue/revenue-sharing/`, data),
    onSuccess: () => {
      refetch();
      form.reset({
        partner: "",
        managementFeeSharingPercentage: 0,
        performanceFeeSharingPercentage: 0,
      });
      setEditingId(null);
      setIsCreating(false);
    },
  });

  const updatePartnerSharing = useMutation({
    mutationKey: ["UPDATE_PARTNER_SHARING"],
    mutationFn: (data) =>
      axiosInstance.patch(`/api/revenue/revenue-sharing/${editingId}/`, data),
    onSuccess: () => {
      refetch();
      form.reset({
        partner: "",
        managementFeeSharingPercentage: 0,
        performanceFeeSharingPercentage: 0,
      });
      setEditingId(null);
      setIsCreating(false);
    },
  });

  const deletePartnerSharing = useMutation({
    mutationKey: ["DELETE_PARTNER_SHARING"],
    mutationFn: (id) =>
      axiosInstance.delete(`/api/revenue/revenue-sharing/${id}/`),
    onSuccess: () => {
      refetch();
      form.reset({
        partner: "",
        managementFeeSharingPercentage: 0,
        performanceFeeSharingPercentage: 0,
      });
      setEditingId(null);
      setIsCreating(false);
    },
  });

  const onSubmit = (values: any) => {
    if (isCreating) {
      createPartnerSharing.mutate({ ...values, feePlan: id });
    } else if (editingId) {
      updatePartnerSharing.mutate({ ...values, feePlan: id });
    }
  };

  const handleEdit = (row: any) => {
    form.reset({
      partner: row.partner?.value || row.partner,
      managementFeeSharingPercentage: row.managementFeeSharingPercentage,
      performanceFeeSharingPercentage: row.performanceFeeSharingPercentage,
    });
    setEditingId(row.id);
    setIsCreating(false);
  };

  const handleDelete = (id: string) => {
    deletePartnerSharing.mutate(id);
  };

  const renderRow = (row: PartnerList) => {
    const isEditing = editingId === row.id;

    return (
      <TableRow key={row.id}>
        <TableBodyCell align="left">
          <Typography fontWeight={500} color="var(--black)">
            {partner?.find((option: any) => row.partner === option.value)?.name}
          </Typography>
        </TableBodyCell>

        <TableBodyCell align="right">
          {isEditing ? (
            <GKTextField
              {...form.register("managementFeeSharingPercentage", {
                valueAsNumber: true,
              })}
              size="small"
              placeholder="Management fee"
              type="number"
              slotProps={{ input: { endAdornment: <PercentageIcon /> } }}
            />
          ) : (
            <Typography fontWeight={500} color="var(--black)">
              {row.managementFeeSharingPercentage}%
            </Typography>
          )}
        </TableBodyCell>

        <TableBodyCell align="right">
          {isEditing ? (
            <GKTextField
              {...form.register("performanceFeeSharingPercentage", {
                valueAsNumber: true,
              })}
              size="small"
              type="number"
              placeholder="Performance fee"
              slotProps={{ input: { endAdornment: <PercentageIcon /> } }}
            />
          ) : (
            <Typography fontWeight={500} color="var(--black)">
              {row.performanceFeeSharingPercentage}%
            </Typography>
          )}
        </TableBodyCell>

        <TableBodyCell align="center">
          {isEditing ? (
            <>
              <IconButton color="success" onClick={form.handleSubmit(onSubmit)}>
                <CheckIcon />
              </IconButton>
            </>
          ) : (
            <>
              <IconButton onClick={() => handleEdit(row)}>
                <Edit1Icon />
              </IconButton>
            </>
          )}
          <IconButton color="error" onClick={() => handleDelete(row.id)}>
            <DeleteIcon />
          </IconButton>
        </TableBodyCell>
      </TableRow>
    );
  };

  return (
    <Grid2 container spacing={1}>
      <Grid2 size={8}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <Cell align="left" style={{ width: "40%" }}>
                  Partner Name
                </Cell>
                <Cell align="right" style={{ width: "23.5%" }}>
                  Management Fees (%)
                </Cell>
                <Cell align="right" style={{ width: "23.5%" }}>
                  Performance Fees (%)
                </Cell>
                <Cell align="center" style={{ width: "15%" }}>
                  Actions
                </Cell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.data?.data || []).map((row: PartnerList) =>
                renderRow(row),
              )}

              {isCreating && (
                <TableRow>
                  <TableBodyCell align="left">
                    <Controller
                      name="partner"
                      control={form.control}
                      rules={{ required: true }}
                      render={({ field, fieldState: { error } }) => (
                        <GKSearchSelect
                          {...field}
                          size="small"
                          disableClearable={false}
                          options={
                            partner?.filter(
                              (item) =>
                                !data?.data?.data
                                  ?.map(
                                    (dataItem: { partner: number }) =>
                                      dataItem?.partner,
                                  )
                                  .includes(item.value),
                            ) || []
                          }
                          onChange={(e, val) => field.onChange(val?.value)}
                          value={
                            partner?.find(
                              (option: any) => field.value === option.value,
                            )?.name || []
                          }
                          getOptionLabel={(option) => option.name || option}
                          error={!!error}
                        />
                      )}
                    />
                  </TableBodyCell>
                  <TableBodyCell align="right">
                    <GKTextField
                      {...form.register("managementFeeSharingPercentage", {
                        valueAsNumber: true,
                      })}
                      size="small"
                      type="number"
                      placeholder="Management fee"
                      slotProps={{
                        input: { endAdornment: <PercentageIcon /> },
                      }}
                    />
                  </TableBodyCell>
                  <TableBodyCell align="right">
                    <GKTextField
                      {...form.register("performanceFeeSharingPercentage", {
                        valueAsNumber: true,
                      })}
                      size="small"
                      type="number"
                      placeholder="Performance fee"
                      slotProps={{
                        input: { endAdornment: <PercentageIcon /> },
                      }}
                    />
                  </TableBodyCell>
                  <TableBodyCell align="center">
                    <IconButton
                      color="success"
                      onClick={form.handleSubmit(onSubmit)}
                    >
                      <CheckIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => {
                        setIsCreating(false);
                        form.reset();
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableBodyCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid2>

      <Grid2 size={12}>
        <GKButton
          startIcon={<CheckIcon />}
          disabled={!!editingId || isCreating}
          onClick={() => {
            setIsCreating(true);
            form.reset();
            setEditingId(null);
          }}
        >
          Add Partner
        </GKButton>
      </Grid2>
    </Grid2>
  );
};

export default PartnerHistory;
